import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Text } from "@otrium/atoms";
import { SxStyleProp, Flex, Box } from "@otrium/core";
import { Locale } from "src/lib/Intl";
import { TrustpilotIcon } from "src/icons/TrustpilotIcon";
import { TrustpilotIconSmall } from "src/icons/TrustpilotIconSmall";
import { TRUSTPILOT_CREDENTIALS_BY_LOCALE } from "src/config";
import {
  getTrustpilotData,
  TrustpilotInterface,
} from "src/lib/getTrustpilotData";
import Link from "next/link";
import NoSSR from "src/atoms/NoSSR";
import { Skeleton } from "src/atoms/Skeleton";

interface Props {
  locale: Locale;
  small?: boolean;
  color?: string;
  sx?: SxStyleProp;
  minScore?: number;
}
const TrustpilotCustom: FC<Props> = ({
  locale,
  small = false,
  color = "white",
  sx = {},
  minScore = 4,
}) => {
  const { link, businessUnitId } = useMemo(
    () => TRUSTPILOT_CREDENTIALS_BY_LOCALE[locale],
    [locale]
  );

  const [score, setScore] = useState<number | null>(() =>
    typeof window !== "undefined"
      ? Number(sessionStorage.getItem(`ot-trustpilot-${businessUnitId}`)) ||
        null
      : null
  );

  const fetchData = useCallback(async () => {
    try {
      const cachedScore =
        typeof window !== "undefined"
          ? sessionStorage.getItem(`ot-trustpilot-${businessUnitId}`)
          : null;

      if (cachedScore) {
        return;
      }

      const data: TrustpilotInterface = await getTrustpilotData(businessUnitId);

      sessionStorage.setItem(
        `ot-trustpilot-${businessUnitId}`,
        `${data.score.trustScore}`
      );

      setScore(data.score.trustScore);
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.error("Error while running `getTrustpilotData`", err);
      }
    }
  }, [businessUnitId]);

  useEffect(() => {
    fetchData().catch((err) => {
      console.error("An error occurred fetching TrustPilot score.", err);
    });
  }, [fetchData]);

  if (locale === "en-us") {
    return null;
  }

  return (
    <NoSSR>
      <Flex
        variant="centered"
        height="100%"
        sx={sx}
        aria-labelledby="trustpilot-label"
      >
        <Text className="sr-only" as="p" id="trustpilot-label">
          Trustpilot score
        </Text>
        {!score ? (
          <Flex variant="centered" fontSize={0} height="100%">
            <Skeleton width="20px" height="16px" />
            <Text ml={small ? "2px" : "0px"} color="tone.anthraciteLight">
              /
            </Text>
            <Text
              sx={{
                position: "relative",
                top: "1px",
              }}
              color="tone.anthraciteLight"
              ml={small ? "2px" : "0px"}
            >
              5
            </Text>
          </Flex>
        ) : score >= minScore ? (
          <Flex variant="centered" fontSize={0} height="100%">
            <Text as="div" fontSize={1} fontWeight={600} color={color}>
              {score}
            </Text>
            <Text ml={small ? "2px" : "0px"} color="tone.anthraciteLight">
              /
            </Text>
            <Text
              sx={{
                position: "relative",
                top: "1px",
              }}
              color="tone.anthraciteLight"
              ml={small ? "2px" : "0px"}
            >
              5
            </Text>
          </Flex>
        ) : null}
        {small ? (
          <Box ml="5px" mb="-4px">
            <Link target="_blank" rel="noopener noreferrer" href={link}>
              <TrustpilotIconSmall color={color} />
            </Link>
          </Box>
        ) : (
          <Box ml={2} mr={2} height="100%" aria-label="Trustpilot website">
            <Link target="_blank" rel="noopener noreferrer" href={link}>
              <TrustpilotIcon color={color} />
            </Link>
          </Box>
        )}
      </Flex>
    </NoSSR>
  );
};

export default TrustpilotCustom;
